import React from 'react';
import {Box} from '@mui/material';
import {Link} from 'react-router-dom';
import {NAVY, PISTE_PROGRESSION, TEXTE, TEXTE_SECONDAIRE} from '../../tokens.js';
import {formatNombre, formatPct, largeurBarre} from './dashboard.selectors.js';
import {carteSx, enteteSectionSx, sousTitreSx, titreSectionSx} from './dashStyles.js';

// R4 : PAV en défaut par commune (top 5, barres orange) | tableau par patrimoine (ligne Territoire en gras).

const LIGNE_COMMUNE = {display: 'grid', gridTemplateColumns: '24px minmax(0, 220px) 1fr 56px', alignItems: 'center', gap: '12px', p: '9px 0', textDecoration: 'none', color: TEXTE, fontSize: 13};
const TH = {fontWeight: 600, p: '10px 12px', borderBottom: '1px solid #EEF1F4', textAlign: 'right', backgroundColor: '#FFFFFF', position: 'sticky', top: 0};
const TD = {p: '12px', borderBottom: '1px solid #EEF1F4', textAlign: 'right'};

export function Communes({communes, lienCommune}) {
    const lignes = [...communes, ...Array(Math.max(0, 5 - communes.length)).fill(null)].slice(0, 5);
    return (
        <Box component="section" aria-labelledby="com-title" sx={carteSx}>
            <Box sx={enteteSectionSx}>
                <Box component="h2" id="com-title" sx={titreSectionSx}>PAV en défaut par commune</Box>
                <Box component="span" sx={sousTitreSx}>top 5 · cliquer filtre la carte</Box>
            </Box>
            <Box component="ol" sx={{m: 0, p: '8px 24px 16px', listStyle: 'none', display: 'flex', flexDirection: 'column'}}>
                {lignes.map((c, i) => (
                    <Box component="li" key={c ? c.cle : `vide-${i}`} sx={{borderBottom: i < 4 ? '1px solid #EEF1F4' : 'none'}}>
                        {c ? (
                            <Box component={Link} to={lienCommune(c.cle)} sx={LIGNE_COMMUNE}>
                                <Box component="span" sx={{color: TEXTE_SECONDAIRE}}>{i + 1}</Box>
                                <Box component="span" sx={{fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{c.commune}</Box>
                                <Box component="span" sx={{height: 10, backgroundColor: PISTE_PROGRESSION, borderRadius: '6px', overflow: 'hidden'}}>
                                    <Box component="span" sx={{display: 'block', width: `${largeurBarre(c.nb, communes)}%`, height: '100%', backgroundColor: '#F39A3F'}}/>
                                </Box>
                                <Box component="span" sx={{textAlign: 'right', fontWeight: 700}}>{formatNombre(c.nb)}</Box>
                            </Box>
                        ) : (
                            <Box sx={LIGNE_COMMUNE}>
                                <Box component="span" sx={{color: TEXTE_SECONDAIRE}}>{i + 1}</Box>
                                <Box component="span">—</Box>
                                <Box component="span" sx={{height: 10, backgroundColor: PISTE_PROGRESSION, borderRadius: '6px'}}/>
                                <Box component="span" sx={{textAlign: 'right'}}>—</Box>
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export function Patrimoines({lignes, lienPatrimoine}) {
    return (
        <Box component="section" aria-labelledby="pat-title" sx={carteSx}>
            <Box sx={enteteSectionSx}>
                <Box component="h2" id="pat-title" sx={titreSectionSx}>Par patrimoine</Box>
                <Box component="span" sx={sousTitreSx}>PAV · service · couverture · en défaut · PAV impossibles</Box>
            </Box>
            <Box sx={{overflow: 'auto', maxHeight: 360}}>
                <Box component="table" sx={{width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13}}>
                    <thead>
                        <Box component="tr" sx={{color: TEXTE_SECONDAIRE, fontSize: 12}}>
                            <Box component="th" scope="col" sx={{...TH, textAlign: 'left', pl: '24px'}}>Patrimoine</Box>
                            <Box component="th" scope="col" sx={TH}>PAV</Box>
                            <Box component="th" scope="col" sx={TH}>Service</Box>
                            <Box component="th" scope="col" sx={TH}>Couverture</Box>
                            <Box component="th" scope="col" sx={TH}>En défaut</Box>
                            <Box component="th" scope="col" sx={{...TH, pr: '24px'}}>PAV impossibles</Box>
                        </Box>
                    </thead>
                    <tbody>
                        {lignes.map((l) => {
                            const total = l.patrimoineId === 'territoire';
                            const cell = total ? {...TD, fontWeight: 700, borderBottom: 'none', position: 'sticky', bottom: 0, backgroundColor: '#FFFFFF', borderTop: '1px solid #EEF1F4'} : TD;
                            return (
                                <tr key={l.patrimoineId}>
                                    <Box component="td" sx={{...cell, textAlign: 'left', pl: '24px'}}>
                                        {total ? l.libelle : (
                                            <>
                                                <Box component={Link} to={lienPatrimoine(l.patrimoineId)} sx={{fontWeight: 600, color: NAVY}}>{l.libelle || '—'}</Box>
                                                <br/>
                                                <Box component="span" sx={{fontSize: 12, color: TEXTE_SECONDAIRE}}>{l.groupe === 'intra' ? 'Intramuros' : l.groupe === 'extra' ? 'Extramuros' : '—'}</Box>
                                            </>
                                        )}
                                    </Box>
                                    <Box component="td" sx={cell}>{formatNombre(l.pav)}</Box>
                                    <Box component="td" sx={cell}>{formatPct(l.service)}</Box>
                                    <Box component="td" sx={cell}>{formatPct(l.couverture)}</Box>
                                    <Box component="td" sx={cell}>{formatNombre(l.enDefaut)}</Box>
                                    <Box component="td" sx={{...cell, pr: '24px'}}>{formatNombre(l.impossibles)}</Box>
                                </tr>
                            );
                        })}
                    </tbody>
                </Box>
            </Box>
        </Box>
    );
}

export default function DashRepartitions({repartitions, lienCommune, lienPatrimoine}) {
    return (
        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '24px', alignItems: 'start'}}>
            <Communes communes={repartitions.communes} lienCommune={lienCommune}/>
            <Patrimoines lignes={repartitions.lignes} lienPatrimoine={lienPatrimoine}/>
        </Box>
    );
}
